import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BRAND from '../config/brand';
import { SERVICES_PATH, INTAKE_PATH } from '../config/planningOffers';
import { trackLinkClick, trackServicesNavClick } from '../lib/analytics';

const NAV_LINKS = [
  { id: 'nav_home', label: 'Home', to: '/links' },
  { id: 'nav_calculators', label: 'Calculators', to: '/calculators' },
  {
    id: 'nav_services_pricing',
    label: 'Services & Pricing',
    to: SERVICES_PATH,
    isServices: true,
  },
];

function isActivePath(pathname, to) {
  if (to === '/links') return pathname === '/links' || pathname === '/linktree';
  if (to === SERVICES_PATH) {
    return pathname === SERVICES_PATH || pathname === '/services-and-pricing';
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

export default function PublicSiteNav({ className = '' }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleNavClick = (item) => {
    if (item.isServices) {
      trackServicesNavClick(item.to, item.label, {
        button_id: item.id,
        button_label: item.label,
      });
      return;
    }
    trackLinkClick(item.to, item.label, {
      destination_type: 'internal',
      button_id: item.id,
      button_label: item.label,
    });
  };

  const planPath = `${INTAKE_PATH}?offer=comprehensive_plan&cta=get_financial_plan&promotional_angle=nav_plan`;

  return (
    <nav
      className={`public-site-nav ${className}`.trim()}
      aria-label="Primary"
    >
      <div className="public-site-nav-inner">
        <Link
          className="public-site-nav-brand"
          to="/links"
          aria-label="Bautista Planning and Analytics home"
          onClick={() =>
            trackLinkClick('/links', 'Home', {
              destination_type: 'internal',
              button_id: 'nav_brand',
              button_label: 'Home',
            })
          }
        >
          <img src={BRAND.logoSrc} alt="" />
          <span>Bautista Planning</span>
        </Link>

        <ul className="public-site-nav-desktop">
          {NAV_LINKS.map((item) => {
            const active = isActivePath(location.pathname, item.to);
            return (
              <li key={item.id}>
                <Link
                  to={item.to}
                  className={[
                    'public-site-nav-link',
                    item.isServices ? 'public-site-nav-link--services' : '',
                    active ? 'is-active' : '',
                  ].filter(Boolean).join(' ')}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => handleNavClick(item)}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li>
            <Link
              className="public-site-nav-cta"
              to={planPath}
              onClick={() =>
                trackLinkClick(planPath, 'Get a Financial Plan', {
                  destination_type: 'contact_form',
                  button_id: 'nav_financial_plan_cta',
                  button_label: 'Get a Financial Plan',
                  promotional_angle: 'nav_plan',
                })
              }
            >
              Get a Financial Plan
            </Link>
          </li>
        </ul>

        <button
          type="button"
          className="public-site-nav-toggle"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="public-site-nav-toggle-bars" aria-hidden="true" />
          <span className="public-site-nav-toggle-label">
            {menuOpen ? 'Close menu' : 'Menu'}
          </span>
        </button>
      </div>

      <div
        id={menuId}
        className={`public-site-nav-mobile ${menuOpen ? 'is-open' : ''}`}
        hidden={!menuOpen}
      >
        <ul className="public-site-nav-mobile-list">
          {NAV_LINKS.map((item) => {
            const active = isActivePath(location.pathname, item.to);
            return (
              <li key={`mobile_${item.id}`}>
                <Link
                  to={item.to}
                  className={[
                    'public-site-nav-mobile-link',
                    item.isServices ? 'public-site-nav-mobile-link--services' : '',
                    active ? 'is-active' : '',
                  ].filter(Boolean).join(' ')}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => handleNavClick({ ...item, id: `mobile_${item.id}` })}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li>
            <Link
              className="public-site-nav-mobile-cta"
              to={planPath}
              onClick={() =>
                trackLinkClick(planPath, 'Get a Financial Plan', {
                  destination_type: 'contact_form',
                  button_id: 'mobile_nav_financial_plan_cta',
                  button_label: 'Get a Financial Plan',
                  promotional_angle: 'nav_plan',
                })
              }
            >
              Get a Financial Plan
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}
